import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  costFormater,
  Preloader,
  Text,
  useResponsive,
  WarningIcon,
} from '@common-fe/common-fe';

import { Modal } from '@/components';
import { useActiveFunds } from '@/modules/investments/Investments/hooks';

import ConfirmModalItem from './ConfirmModalItem';
import ConfirmModalListHeader from './ConfirmModalListHeader';
interface Props {
  accountId: string;
  visible: boolean;
  isLoading?: boolean;
  onSetVisible: (value: boolean) => void;
  onSubmit: () => void;
}
const ConfirmFullLiquidation: React.FC<Props> = ({
  visible,
  onSetVisible,
  onSubmit,
  accountId,
  isLoading,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const handleClose = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const { data } = useActiveFunds({ accountId });
  const filtetedData = useMemo(() => {
    return data.filter((item) => item.amount);
  }, [data]);
  const totalAmount = useMemo(() => {
    return data.reduce((result, item) => {
      return result + (item.amount || 0);
    }, 0);
  }, [data]);

  return (
    <Modal visible={visible} testId="confirm-liquidation" onSetVisible={onSetVisible}>
      <Box direction="column">
        <Text size="2xl" textAlign="center" weight={700}>
          {t('Confirm')}
        </Text>
      </Box>
      <Box
        direction="column"
        background="module"
        pad={isMobile ? 'spacing8' : 'spacing24'}
        round="moduleRound"
      >
        {!isMobile && <ConfirmModalListHeader />}
        <Box direction="column">
          {filtetedData.map((item) => (
            <ConfirmModalItem
              testId={`confirm-liquidation_${item.id}`}
              value={item.amount || 0}
              key={item.id}
              {...item}
            />
          ))}
        </Box>
        <Box
          direction="row"
          pad={isMobile ? 'spacing12' : { vertical: '6px', horizontal: 'spacing24' }}
          background="border1"
          margin={{ top: 'xs', bottom: isMobile ? 'spacing12' : 'spacing24' }}
          round="container2Round"
          justify="between"
        >
          <Text size={isMobile ? 'large' : 'medium'} weight={700} style={{ flex: 2 }}>
            {t('Total')}
          </Text>
          <Text
            data-testId="confirm-liquidation_total-amount"
            size={isMobile ? 'large' : 'medium'}
            weight={700}
            textAlign="end"
          >
            {costFormater(totalAmount, {
              isPrecision: true,
            })}
          </Text>
        </Box>

        <Box
          background="canvas"
          border={{ color: 'border2', size: 'small' }}
          round="container1Round"
          pad="spacing12"
          elevation="default"
          style={{ position: 'relative' }}
        >
          <Box
            height="2px"
            round="small"
            background="warning"
            style={{ position: 'absolute', top: '-0.5px', left: '12px', right: '12px' }}
          />
          <Box
            direction="row"
            align="center"
            background="warningContainer"
            border={{ color: 'warning', size: 'small' }}
            round="container2Round"
            pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
          >
            <div>
              <Box>
                <WarningIcon />
              </Box>
            </div>
            <Text margin={{ left: 'spacing8' }} data-testid="sell-investments_confirm-liquidation_sub-title">
              {t(
                'Sells can take up to 2 to 3 business days to be reflected in your account. Any automatic investments will be paused.'
              )}
            </Text>
          </Box>
        </Box>
      </Box>

      <Box
        direction="row"
        justify="end"
        gap="spacing8"
        margin={{ top: isMobile ? 'spacing12' : 'spacing24' }}
      >
        <Box fill={isMobile}>
          <AppButton
            fill={isMobile}
            buttonType="secondary"
            width="140px"
            testId="confirm-liquidation_cancel"
            onClick={handleClose}
          >
            {t('Back')}
          </AppButton>
        </Box>
        <Box fill={isMobile}>
          <AppButton
            fill={isMobile}
            testId="confirm-liquidation_confirm"
            disabled={isLoading}
            width="140px"
            onClick={onSubmit}
          >
            {isLoading ? <Preloader color="white" /> : t('Confirm')}
          </AppButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmFullLiquidation;
